/** @format */

import type React from "react";
import { useEffect, useRef } from "react";
import { theme } from "senf-atomic-design-system";

interface NoLocationMapOverlayProps {
  statefulMap: any;
  ideaMap: any;
  formik: any;
}

const NoLocationMapOverlay: React.FC<NoLocationMapOverlayProps> = ({
  statefulMap,
  ideaMap,
  formik,
}) => {
  const layerAddedRef = useRef(false);
  const sourceId = "no-location-preview";

  useEffect(() => {
    if (!statefulMap || !ideaMap?.mapbounds || layerAddedRef.current) {
      return;
    }

    const map = statefulMap;
    const topicColor =
      ideaMap?.customTopicsObject?.[formik?.values?.topic]?.color ||
      theme.colors.primary.primary100;

    const addNoLocationLayer = () => {
      try {
        // Create the map bounds data with the selected topic color
        const mapBoundsWithColor = {
          type: "FeatureCollection",
          features: ideaMap.mapbounds.features.map((feature) => ({
            ...feature,
            properties: {
              ...feature.properties,
              color: topicColor,
              opacity: 0.3,
              drawType: "area",
            },
          })),
        };

        // Add source if it doesn't exist
        if (!map.getSource(sourceId)) {
          map.addSource(sourceId, {
            type: "geojson",
            data: mapBoundsWithColor,
          });

          // Add fill layer
          map.addLayer({
            id: `${sourceId}-fill`,
            source: sourceId,
            type: "fill",
            paint: {
              "fill-color": ["get", "color"],
              "fill-opacity": ["get", "opacity"],
            },
          });

          // Add outline layer
          map.addLayer({
            id: `${sourceId}-outline`,
            source: sourceId,
            type: "line",
            paint: {
              "line-color": ["get", "color"],
              "line-width": 2,
              "line-opacity": 0.8,
            },
          });

          layerAddedRef.current = true;
        } else {
          // Update existing source with new color
          map.getSource(sourceId).setData(mapBoundsWithColor);
        }
      } catch (error) {
        console.error("Error adding no-location map overlay:", error);
      }
    };

    if (map.isStyleLoaded()) {
      addNoLocationLayer();
    } else {
      map.on("load", addNoLocationLayer);
    }

    // Cleanup function
    return () => {
      if (map?.getSource?.(sourceId)) {
        try {
          if (map.getLayer(`${sourceId}-fill`)) {
            map.removeLayer(`${sourceId}-fill`);
          }
          if (map.getLayer(`${sourceId}-outline`)) {
            map.removeLayer(`${sourceId}-outline`);
          }
          map.removeSource(sourceId);
          layerAddedRef.current = false;
        } catch (error) {
          console.error("Error cleaning up no-location map overlay:", error);
        }
      }
    };
  }, [
    statefulMap,
    ideaMap?.mapbounds,
    ideaMap?.customTopicsObject,
    formik?.values?.topic,
  ]);

  // This component doesn't render any JSX, it only manages map layers
  return null;
};

export default NoLocationMapOverlay;
